/**
 * GroupSubscriptionOverview
 *
 * Subscription tab layout for group-practice orgs. Renders the prototype's
 * tier-comparison + add-on summary on the left, the ManagementSidebar on the
 * right (sticky on lg+). All plan changes happen in the Members tab; this view
 * is read-only for tier/add-on assignment.
 *
 * Used by `SubscriptionsBundlingPage` when `useOrganizationInfo()?.group` is
 * true. Solo orgs get a different layout (GDN-1641).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Tune from '@mui/icons-material/Tune';
import { Box, Button, Divider, Grid, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import type {
  FullSubscriptionDetailsResponse,
  SubscriptionLineItem,
} from '@store/generated/subscriptions';

import { useGetInvoicesQuery, useUpdateBillingIntervalMutation } from '../store/api';
import { useBundleCatalog } from '../store/hooks';
import type { BillingInterval, BundleWithStatus } from '../types';
import { getAddOnsIncludedInTier, getMonthlyAmountCents } from '../utils';

import { BillingIntervalToggle } from './BillingIntervalToggle';
import { CancelSubscriptionSection } from './CancelSubscriptionSection';
import { InvoiceHistoryModal } from './InvoiceHistoryModal';
import { ManagementAddOnCard } from './ManagementAddOnCard';
import { ManagementSidebar } from './ManagementSidebar';
import { ManagementTierCard } from './ManagementTierCard';

interface GroupSubscriptionOverviewProps {
  subscription: FullSubscriptionDetailsResponse;
  onUpdatePaymentMethod: () => void;
  onSubscriptionStatusChange: () => void;
}

const INVOICE_FETCH_LIMIT = 5;

const computeMonthlyTotal = (
  lineItems: SubscriptionLineItem[],
  bundleMap: Map<string, BundleWithStatus>,
  interval: BillingInterval,
): number =>
  lineItems.reduce((sum, item) => {
    if (!item.bundle_id) return sum;
    const bundle = bundleMap.get(item.bundle_id);
    if (!bundle) return sum;
    return sum + getMonthlyAmountCents(bundle, interval === 'year') * (item.quantity || 1);
  }, 0);

export const GroupSubscriptionOverview: React.FC<GroupSubscriptionOverviewProps> = ({
  subscription,
  onUpdatePaymentMethod,
  onSubscriptionStatusChange,
}) => {
  const navigate = useNavigate();
  const { subscriptionManagement } = useEventTracking();
  const [updateBillingInterval, { isLoading: isUpdatingInterval }] =
    useUpdateBillingIntervalMutation();
  const [previewInterval, setPreviewInterval] = useState<BillingInterval | null>(null);
  const [invoiceHistoryOpen, setInvoiceHistoryOpen] = useState(false);

  const { tiers, addOns, annualDiscountRange, allBundles } = useBundleCatalog();
  const { data: invoicesData } = useGetInvoicesQuery({ limit: INVOICE_FETCH_LIMIT });

  // Track tier comparison view once per session/mount
  const hasFiredViewedRef = useRef(false);
  useEffect(() => {
    if (hasFiredViewedRef.current) return;
    hasFiredViewedRef.current = true;
    subscriptionManagement.tierComparisonViewed({
      subscription_status: subscription.subscription_status,
      billing_interval: subscription.billing_interval,
      is_trialing: subscription.is_trialing,
      tier_count: tiers.length,
      addon_count: addOns.length,
    });
  }, [subscription, tiers.length, addOns.length, subscriptionManagement]);

  // Quick lookup of line items by bundle_id, plus catalog map
  const lineItemsByBundle = useMemo(() => {
    const map = new Map<string, SubscriptionLineItem>();
    subscription.line_items.forEach(item => {
      if (item.bundle_id) map.set(item.bundle_id, item);
    });
    return map;
  }, [subscription.line_items]);

  const bundleMap = useMemo(() => {
    const map = new Map<string, BundleWithStatus>();
    allBundles.forEach(bundle => map.set(bundle.bundle_id, bundle));
    return map;
  }, [allBundles]);

  const totalMemberSeats = useMemo(
    () =>
      subscription.line_items
        .filter(item => item.bundle_type === 'tier')
        .reduce((sum, item) => sum + (item.quantity || 0), 0),
    [subscription.line_items],
  );

  // Billing interval — preview vs actual
  const billingInterval: BillingInterval =
    subscription.billing_interval === 'year' ? 'year' : 'month';
  const displayInterval: BillingInterval = previewInterval ?? billingInterval;
  const isPreviewMode = previewInterval !== null && previewInterval !== billingInterval;

  // Pending-change preview for the sidebar
  const previewMonthlyCents = useMemo(
    () =>
      isPreviewMode
        ? computeMonthlyTotal(subscription.line_items, bundleMap, displayInterval)
        : null,
    [isPreviewMode, subscription.line_items, bundleMap, displayInterval],
  );

  const handleConfirmIntervalChange = async () => {
    if (!previewInterval || previewInterval === billingInterval) return;
    try {
      await updateBillingInterval({
        changeBillingIntervalRequest: { billing_interval: previewInterval },
      }).unwrap();
      subscriptionManagement.billingIntervalConfirmed({
        from_interval: billingInterval,
        to_interval: previewInterval,
      });
      setPreviewInterval(null);
    } catch {
      // Error surfacing handled by parent toast/snackbar in future iteration
      setPreviewInterval(null);
    }
  };

  const handleGoToMembers = () => {
    navigate('/practice/clinicians');
  };

  const handleShowAllInvoices = () => {
    subscriptionManagement.invoiceHistoryOpened({ org_type: 'group' });
    setInvoiceHistoryOpen(true);
  };

  const handleCloseInvoiceHistory = () => {
    subscriptionManagement.invoiceHistoryClosed({ org_type: 'group' });
    setInvoiceHistoryOpen(false);
  };

  return (
    <Grid container spacing={3}>
      {/* Left column: tier comparison + add-on summary + edit-plans + cancel */}
      <Grid size={{ xs: 12, lg: 8 }}>
        {/* Billing interval toggle (right-aligned) */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <BillingIntervalToggle
            value={displayInterval}
            onChange={interval => {
              if (interval === billingInterval) setPreviewInterval(null);
              else setPreviewInterval(interval);
            }}
            size="small"
            discountRange={annualDiscountRange}
            disabled={isUpdatingInterval}
          />
        </Box>

        {/* Tier comparison */}
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Tier Comparison
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Reference for what each tier includes. To change a member&apos;s tier, go to the{' '}
          <Typography
            component="span"
            variant="body2"
            onClick={handleGoToMembers}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'underline',
              '&:hover': { color: 'primary.dark' },
            }}
          >
            Members
          </Typography>{' '}
          tab.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 4,
          }}
        >
          {tiers.map(tier => {
            const lineItem = lineItemsByBundle.get(tier.bundle_id);
            const count = lineItem?.quantity ?? 0;
            const isActive = count > 0;
            return (
              <ManagementTierCard
                key={tier.bundle_id}
                bundle={tier}
                billingInterval={displayInterval}
                includedAddons={getAddOnsIncludedInTier(tier, addOns)}
                isActive={isActive}
                statusChip={{
                  label: isActive ? `${count} of ${totalMemberSeats}` : 'None',
                  color: isActive ? 'primary' : 'default',
                }}
              />
            );
          })}
        </Box>

        {/* Add-ons */}
        <Typography variant="h6" sx={{ mb: 0.5 }}>
          Add-ons
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Add-ons active across your team. To change add-ons, go to the{' '}
          <Typography
            component="span"
            variant="body2"
            onClick={handleGoToMembers}
            sx={{
              color: 'primary.main',
              cursor: 'pointer',
              fontWeight: 500,
              textDecoration: 'underline',
              '&:hover': { color: 'primary.dark' },
            }}
          >
            Members
          </Typography>{' '}
          tab.
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 2,
            mb: 4,
          }}
        >
          {addOns.map(addon => {
            const lineItem = lineItemsByBundle.get(addon.bundle_id);
            const count = lineItem?.quantity ?? 0;
            const isActive = count > 0;
            const price = getMonthlyAmountCents(addon, displayInterval === 'year');
            return (
              <ManagementAddOnCard
                key={addon.bundle_id}
                bundle={addon}
                billingInterval={displayInterval}
                isActive={isActive}
                statusChip={{
                  label: isActive ? `${count} of ${totalMemberSeats}` : 'None',
                  color: isActive ? 'primary' : 'default',
                }}
                priceSuffix="/member/mo"
                subtotalCents={isActive ? price * count : undefined}
              />
            );
          })}
        </Box>

        {/* Edit plans shortcut */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<Tune />}
            onClick={handleGoToMembers}
          >
            Edit plans on Members tab
          </Button>
        </Box>
      </Grid>

      {/* Right column: sticky ManagementSidebar.
          At lg+ this sits beside the top-left content; at smaller breakpoints it
          stacks between the top content and the danger zone. */}
      <Grid size={{ xs: 12, lg: 4 }}>
        <ManagementSidebar
          subscription={subscription}
          invoices={invoicesData?.invoices}
          pendingChange={
            isPreviewMode && previewMonthlyCents != null && previewInterval
              ? {
                  newMonthlyCents: previewMonthlyCents,
                  newBillingInterval: previewInterval,
                  onConfirm: handleConfirmIntervalChange,
                  onRevert: () => setPreviewInterval(null),
                  isConfirming: isUpdatingInterval,
                }
              : undefined
          }
          onUpdatePaymentMethod={onUpdatePaymentMethod}
          onShowAllInvoices={handleShowAllInvoices}
        />
      </Grid>

      {/* Danger zone — own Grid row so on narrow viewports it lands below the
          sidebar instead of above it. At lg+ it occupies the left column on a
          second row, leaving the sticky sidebar uninterrupted. */}
      <Grid size={{ xs: 12, lg: 8 }}>
        <Divider sx={{ mb: 3 }} />
        <CancelSubscriptionSection
          cancelAtPeriodEnd={subscription.cancel_at_period_end ?? undefined}
          currentPeriodEnd={subscription.current_period_end}
          subscriptionStatus={subscription.subscription_status}
          onStatusChange={onSubscriptionStatusChange}
        />
      </Grid>

      <InvoiceHistoryModal open={invoiceHistoryOpen} onClose={handleCloseInvoiceHistory} />
    </Grid>
  );
};
